import { useState } from "react";

import { GearIcon } from "@radix-ui/react-icons";
import { useAtom } from "jotai";
import { ChevronsUpDown, Plus, PlusCircle } from "lucide-react";

import {
  isStoreSyncPausedForBilling,
  StoreSyncPausedNotice,
} from "src/components/Billing/StoreSyncPausedNotice";
import { PaywallDialog } from "src/components/PaywallDialog/PaywallDialog";
import { settingsDialogAtom } from "src/components/SettingsDialog/SettingsDialog";
import { StoreAvatar } from "src/components/StoreAvatar/StoreAvatar";
import { Button } from "src/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { ScrollArea } from "src/components/ui/scroll-area";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "src/components/ui/sidebar";
import { Skeleton } from "src/components/ui/skeleton";
import { storeIntegrationTypes } from "src/lib/constants";
import { useAdminPrivacyMode } from "src/lib/hooks/useAdminPrivacyMode";
import { useIntegration } from "src/lib/hooks/useIntegration";
import { cn } from "src/lib/utils";

export function StoreSwitcher({ dropdownPosition, className, noSettings }) {
  const { isMobile } = useSidebar();
  const {
    selectedIntegration: activeStore,
    setSelectedIntegration: setActiveStore,
    integrations: stores,
    loading,
  } = useIntegration(storeIntegrationTypes.shopify);
  const [menuOpen, setMenuOpen] = useState(false);
  const [, setSettingsAtomStore] = useAtom(settingsDialogAtom);
  const { privacyMode } = useAdminPrivacyMode();

  if (!activeStore) {
    if (loading) {
      return <Skeleton className="h-12 w-full" />;
    }

    return (
      <PaywallDialog
        onClick={() => {
          window.open(process.env.SHOPIFY_LISTING_URL, "_blank");
        }}
      >
        <Button>
          <PlusCircle className="size-4" />
          Add Store
        </Button>
      </PaywallDialog>
    );
  }

  const activeStoreIndex = stores.findIndex(
    (store) => store.id === activeStore.id
  );
  const activeStoreName = privacyMode
    ? process.env.APP_NAME
    : activeStore.description || `Store ${activeStoreIndex + 1}`;
  const activeStoreDomain = privacyMode
    ? "lucris.io"
    : activeStore.domain || "-";

  let dropdownAlign = "start";
  let dropdownSide = isMobile ? "bottom" : "right";
  let dropdownWidth = "min-w-56";

  if (dropdownPosition) {
    switch (dropdownPosition) {
      case "left":
        dropdownAlign = "end";
        dropdownSide = "left";
        break;
      case "bottom-left":
        dropdownAlign = "start";
        dropdownSide = "bottom";
        break;
      case "bottom-right":
        dropdownAlign = "end";
        dropdownSide = "bottom";
        break;
    }
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={true}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className={cn(
                "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer",
                className
              )}
              noAutoOpen
            >
              <StoreAvatar
                integrationId={activeStore.id}
                storeName={activeStoreName}
                isDemo={activeStore.isDemo}
                className="size-8 text-sm"
              />
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {activeStoreName}
                </span>
                <span className="truncate text-xs">{activeStoreDomain}</span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className={dropdownWidth + " rounded-lg"}
            align={dropdownAlign}
            side={dropdownSide}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-muted-foreground flex w-full justify-between text-xs">
              Stores
              {!noSettings && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="group -m-2 -mr-2.5 h-8 w-8 p-2"
                  onClick={() => {
                    setSettingsAtomStore((prev) => ({
                      ...prev,
                      open: true,
                      activeTab: "store",
                      manageParam: null,
                      sectionTarget: null,
                      sectionTargetNonce: 0,
                    }));

                    setMenuOpen(false);
                  }}
                >
                  <GearIcon className="size-4 p-0 transition-all duration-500 group-hover:rotate-[420deg]" />
                </Button>
              )}
            </DropdownMenuLabel>

            <ScrollArea className="flex max-h-92 flex-col">
              {stores.map((store, index) => {
                const storeName = privacyMode
                  ? `${process.env.APP_NAME} ${index + 1}`
                  : store.description || `Store ${index + 1}`;
                return (
                  <DropdownMenuItem
                    key={store.id}
                    onClick={() => {
                      if (!loading) {
                        setActiveStore(store);
                      }
                    }}
                    className="gap-2 p-2"
                  >
                    <StoreAvatar
                      integrationId={store.id}
                      storeName={storeName}
                      isDemo={store.isDemo}
                      className="size-6 rounded-md text-xs"
                    />
                    <span className="min-w-0 flex-1 truncate">{storeName}</span>
                    {isStoreSyncPausedForBilling(store) && (
                      <StoreSyncPausedNotice
                        store={store}
                        variant="icon"
                        side="right"
                        align="center"
                        showAction={false}
                        preventParentInteraction
                        className="shrink-0"
                      />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </ScrollArea>
            <DropdownMenuSeparator />
            <PaywallDialog
              onClick={() => {
                window.open(process.env.SHOPIFY_LISTING_URL, "_blank");
              }}
            >
              <DropdownMenuItem
                className="gap-2 p-2"
                onSelect={(e) => {
                  e.preventDefault();
                }}
              >
                <div className="bg-background flex size-6 items-center justify-center rounded-md border">
                  <Plus className="size-4" />
                </div>
                <div className="text-muted-foreground font-medium">
                  Add Store
                </div>
              </DropdownMenuItem>
            </PaywallDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
